import type { FmvRecord, PriceSnapshot } from "../../components/fmv/FmvTableComponent";
import type {
  VehicleRecord,
  VehiclePriceSnapshot,
  VehiclePayload,
  BulkUpsertPayload,
} from "../interfaces/VehiclesInterfaces";

// ── Helpers ──

const toNum = (val: string | number | null | undefined): number | null => {
  if (val === null || val === undefined || val === "") return null;
  const n = typeof val === "string" ? parseFloat(val) : val;
  return isNaN(n) ? null : n;
};

// ── API → Frontend ──

const snapshotToPrice = (s: VehiclePriceSnapshot) => ({
  discounted_cash_price: toNum(s.discounted_cash_price),
  target_buying: toNum(s.target_buying_price),
  gdfi_fmv: toNum(s.gdfi_fmv_price),
  gdfi_fin_p: toNum(s.gdfi_fin_price),
  tfs_fin_p: toNum(s.tfs_fin_price),
  pre_order_cash_price: toNum(s.preorder_cash_price),
  reserve_fin_p: toNum(s.reserve_fin_price),
});

const snapshotToHistory = (s: VehiclePriceSnapshot): PriceSnapshot => ({
  date: s.snapshot_date || "",
  ...snapshotToPrice(s),
});

/**
 * Transform a single VehicleRecord (API shape) → FmvRecord (frontend shape).
 */
export const vehicleToFmvRecord = (v: VehicleRecord): FmvRecord => {
  const defaultSnapshot = v.priceSnapshots?.find((s) => s.is_default === 1);
  const historicalSnapshots = (v.priceSnapshots || [])
    .filter((s) => s.is_default === 0)
    .map(snapshotToHistory);

  const prices = defaultSnapshot
    ? snapshotToPrice(defaultSnapshot)
    : {
        discounted_cash_price: null,
        target_buying: null,
        gdfi_fmv: null,
        gdfi_fin_p: null,
        tfs_fin_p: null,
        pre_order_cash_price: null,
        reserve_fin_p: null,
      };

  return {
    key: v.bmtytl_code,
    vehicle_id: v.vehicle_id,
    brand: v.brand,
    model: v.model,
    trim: v.trim,
    model_year: v.model_year,
    transmission: v.transmission,
    location: v.location,
    BMTYTL: v.bmtytl_code,
    tfs_srp: toNum(v.tfs_srp),
    ...prices,
    snapshots: historicalSnapshots,
  };
};

/**
 * Transform an array of VehicleRecords → FmvRecords.
 */
export const vehiclesToFmvRecords = (vehicles: VehicleRecord[]): FmvRecord[] =>
  vehicles.map(vehicleToFmvRecord);

// ── Frontend → API ──

/**
 * Transform a single FmvRecord → VehiclePayload (for create / update).
 */
export const fmvRecordToPayload = (r: FmvRecord): VehiclePayload => {
  const tfsSrp = toNum(r.tfs_srp);

  return {
    brand: r.brand,
    model: r.model,
    trim: r.trim,
    model_year: r.model_year,
    transmission: r.transmission,
    location: r.location,
    discounted_cash_price: toNum(r.discounted_cash_price),
    target_buying_price: toNum(r.target_buying),
    gdfi_fmv_price: toNum(r.gdfi_fmv),
    gdfi_fin_price: toNum(r.gdfi_fin_p),
    tfs_fin_price: toNum(r.tfs_fin_p),
    preorder_cash_price: toNum(r.pre_order_cash_price),
    reserve_fin_price: toNum(r.reserve_fin_p),
    // Only send TFS SRP when a value was given, so a missing column or blank
    // cell never clears an existing SRP.
    ...(tfsSrp !== null && { tfs_srp: tfsSrp }),
  };
};

/**
 * Transform an array of FmvRecords → BulkUpsertPayload.
 */
export const fmvRecordsToBulkPayload = (
  records: FmvRecord[]
): BulkUpsertPayload => ({
  items: records.map(fmvRecordToPayload),
});
